import React from 'react';
import styled from 'styled-components';
import { tokens } from '../../design-system/tokens';
import { Link } from 'react-router-dom';

const Container = styled.div`
  background-color: ${tokens.colors.neutral[0]};
  min-height: 100vh;
  padding: ${tokens.spacing[10]} ${tokens.spacing[5]};

  @media (max-width: ${tokens.breakpoints.md}) {
    padding: ${tokens.spacing[8]} ${tokens.spacing[4]};
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.spacing[2]};
  padding: ${tokens.spacing[3]} ${tokens.spacing[5]};
  background-color: ${tokens.colors.neutral[100]};
  border: 1px solid ${tokens.colors.neutral[200]};
  border-radius: ${tokens.borderRadius.lg};
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[700]};
  text-decoration: none;
  transition: all ${tokens.animation.duration.fast} ${tokens.animation.easing.ease};
  margin-bottom: ${tokens.spacing[8]};

  &:hover {
    background-color: ${tokens.colors.neutral[200]};
  }
`;

const Title = styled.h1`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize['3xl']};
  font-weight: ${tokens.typography.fontWeight.bold};
  color: ${tokens.colors.neutral[900]};
  margin-bottom: ${tokens.spacing[8]};
  word-break: keep-all;
`;

const Section = styled.section`
  margin-bottom: ${tokens.spacing[10]};
`;

const SectionTitle = styled.h2`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.xl};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[4]};
  word-break: keep-all;
`;

const SubSection = styled.div`
  margin: ${tokens.spacing[6]} 0;
`;

const SubTitle = styled.h3`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.medium};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[3]};
  word-break: keep-all;
`;

const Paragraph = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  line-height: 1.8;
  color: ${tokens.colors.neutral[700]};
  margin-bottom: ${tokens.spacing[4]};
  word-break: keep-all;
`;

const List = styled.ul`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.base};
  line-height: 1.8;
  color: ${tokens.colors.neutral[700]};
  margin: ${tokens.spacing[4]} 0;
  padding-left: ${tokens.spacing[6]};
  list-style-type: disc;

  li {
    margin-bottom: ${tokens.spacing[2]};
    word-break: keep-all;
  }

  ul {
    margin-top: ${tokens.spacing[2]};
    margin-bottom: 0;
    list-style-type: circle;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: ${tokens.spacing[4]} 0;
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  overflow-x: auto;
  display: block;

  @media (min-width: ${tokens.breakpoints.md}) {
    display: table;
  }

  thead {
    background-color: ${tokens.colors.neutral[100]};
  }

  th, td {
    border: 1px solid ${tokens.colors.neutral[300]};
    padding: ${tokens.spacing[3]};
    text-align: left;
    word-break: keep-all;
  }

  th {
    font-weight: ${tokens.typography.fontWeight.semibold};
    color: ${tokens.colors.neutral[800]};
    background-color: ${tokens.colors.neutral[100]};
  }

  td {
    color: ${tokens.colors.neutral[700]};
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin: ${tokens.spacing[4]} 0;
`;

const UpdateDate = styled.p`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.sm};
  color: ${tokens.colors.neutral[500]};
  margin-bottom: ${tokens.spacing[6]};
`;

const ContactInfo = styled.div`
  background-color: ${tokens.colors.neutral[100]};
  border-radius: ${tokens.borderRadius.lg};
  padding: ${tokens.spacing[5]};
  margin: ${tokens.spacing[4]} 0;
`;

const ContactTitle = styled.h3`
  font-family: ${tokens.typography.fontFamily.primary};
  font-size: ${tokens.typography.fontSize.lg};
  font-weight: ${tokens.typography.fontWeight.semibold};
  color: ${tokens.colors.neutral[800]};
  margin-bottom: ${tokens.spacing[3]};
`;

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    font-family: ${tokens.typography.fontFamily.primary};
    font-size: ${tokens.typography.fontSize.base};
    color: ${tokens.colors.neutral[700]};
    margin-bottom: ${tokens.spacing[2]};
  }
`;

const InfoBox = styled.div`
  background-color: ${tokens.colors.primary[50]};
  border-left: 4px solid ${tokens.colors.primary[500]};
  padding: ${tokens.spacing[4]};
  margin: ${tokens.spacing[4]} 0;
  border-radius: ${tokens.borderRadius.md};

  p {
    margin: 0;
    font-size: ${tokens.typography.fontSize.sm};
    color: ${tokens.colors.neutral[700]};
  }
`;

export const PrivacyPolicy: React.FC = () => {
  return (
    <Container>
      <ContentWrapper>
        <BackButton to="/">← 홈으로 돌아가기</BackButton>

        <Title>럭키온(LuckyOn) 개인정보처리방침</Title>
        <UpdateDate>시행일: 2025년 10월 7일</UpdateDate>

        <Section>
          <SectionTitle>제1조 (개인정보의 처리목적)</SectionTitle>
          <Paragraph>
            카인드바이럴(이하 "회사")이 운영하는 럭키온(LuckyOn) 서비스는 다음의 목적을 위하여 개인정보를 처리합니다.
            처리하는 개인정보는 다음의 목적 이외의 용도로는 이용되지 않으며, 이용 목적이 변경되는 경우에는
            개인정보보호법 제18조에 따라 별도의 동의를 받는 등 필요한 조치를 이행할 예정입니다.
          </Paragraph>

          <List>
            <li>
              <strong>회원 관리</strong>: 회원제 서비스 제공, 본인 식별·인증, 회원자격 유지·관리, 서비스 부정이용 방지
            </li>
            <li>
              <strong>운세 서비스 제공</strong>: 개인 맞춤형 운세 콘텐츠 생성 및 제공
              (오늘의 운세, 사주팔자, 토정비결, 궁합, 연애운, 재물운)
            </li>
            <li>
              <strong>유료서비스 운영</strong>: 결제 처리, 구독 서비스 관리, 일회성 구매 처리, 환불 처리
            </li>
            <li>
              <strong>추천 프로그램 운영</strong>: 추천인 코드 관리, 추천 수익 정산
            </li>
            <li>
              <strong>고충처리</strong>: 민원인의 신원 확인, 민원사항 확인, 사실조사를 위한 연락·통지, 처리결과 통보
            </li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제2조 (처리하는 개인정보의 항목)</SectionTitle>
          <Paragraph>회사는 다음의 개인정보 항목을 처리하고 있습니다.</Paragraph>

          <SubSection>
            <SubTitle>1. 회원가입 시</SubTitle>
            <List>
              <li><strong>필수항목</strong>: 이메일, 비밀번호, 이름</li>
              <li><strong>선택항목</strong>: 전화번호, 추천인 코드</li>
            </List>
          </SubSection>

          <SubSection>
            <SubTitle>2. 운세 서비스 이용 시</SubTitle>
            <List>
              <li><strong>공통</strong>: 이름, 생년월일</li>
              <li><strong>사주팔자</strong>: 생시, 성별 (선택)</li>
              <li><strong>토정비결</strong>: 음력/양력 구분</li>
              <li><strong>궁합</strong>: 본인 및 상대방 정보</li>
              <li><strong>연애운</strong>: 성별, 연애상태</li>
              <li><strong>재물운</strong>: 직업 유형</li>
            </List>
            <InfoBox>
              <p>
                ※ 수집된 개인정보는 별도의 본인 인증 절차 없이 입력하신 내용 그대로 서비스 제공에 이용됩니다.
              </p>
            </InfoBox>
          </SubSection>

          <SubSection>
            <SubTitle>3. 결제 시</SubTitle>
            <List>
              <li>결제 거래번호, 결제 금액, 결제 일시</li>
              <li>구매 서비스명 또는 구독 기간</li>
              <li>
                <strong>금융정보 (카드번호, 계좌번호 등)</strong>는 회사가 보관하지 않으며,
                결제대행업체(PG사)에서 직접 처리합니다.
              </li>
            </List>
          </SubSection>

          <SubSection>
            <SubTitle>4. 추천 프로그램 참여 시</SubTitle>
            <List>
              <li>추천인 관계 정보</li>
              <li>추천 수익 정산 내역</li>
              <li><strong>출금 요청 시</strong>: 은행명, 계좌번호, 예금주명</li>
            </List>
          </SubSection>

          <SubSection>
            <SubTitle>5. 자동 수집 정보</SubTitle>
            <List>
              <li>서비스 이용 기록, 접속 로그, 접속 IP 정보</li>
              <li>쿠키, 접속일시, 서비스 이용 기록</li>
            </List>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>제3조 (개인정보의 처리 및 보유기간)</SectionTitle>
          <Paragraph>
            회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은
            개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.
          </Paragraph>

          <SubSection>
            <SubTitle>1. 회원 정보</SubTitle>
            <Paragraph>
              회원 탈퇴 시까지 보유하며, 탈퇴 즉시 파기합니다.
              단, 관계 법령에 따라 보존할 필요가 있는 경우 해당 법령에서 정한 기간 동안 보관합니다.
            </Paragraph>
          </SubSection>

          <SubSection>
            <SubTitle>2. 운세 서비스 결과</SubTitle>
            <Paragraph>
              서비스 제공을 위한 합리적인 기간 동안 보관 후 자동 삭제됩니다.
              회원 탈퇴 시 즉시 파기됩니다.
            </Paragraph>
          </SubSection>

          <SubSection>
            <SubTitle>3. 법령에 따른 보존</SubTitle>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <th>보존 항목</th>
                    <th>근거 법령</th>
                    <th>보존 기간</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>계약 또는 청약철회 기록</td>
                    <td>전자상거래법</td>
                    <td>5년</td>
                  </tr>
                  <tr>
                    <td>대금결제 및 재화 공급 기록</td>
                    <td>전자상거래법</td>
                    <td>5년</td>
                  </tr>
                  <tr>
                    <td>소비자 불만 및 분쟁 처리 기록</td>
                    <td>전자상거래법</td>
                    <td>3년</td>
                  </tr>
                  <tr>
                    <td>접속 로그 기록</td>
                    <td>통신비밀보호법</td>
                    <td>3개월</td>
                  </tr>
                </tbody>
              </Table>
            </TableWrapper>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>제4조 (개인정보의 제3자 제공)</SectionTitle>
          <Paragraph>
            회사는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다.
            다만, 다음의 경우에는 예외로 합니다.
          </Paragraph>
          <List>
            <li>이용자가 사전에 동의한 경우</li>
            <li>법령의 규정에 의거하거나, 수사 목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제5조 (개인정보처리의 위탁)</SectionTitle>
          <Paragraph>회사는 서비스 이행을 위해 다음과 같이 개인정보 처리를 위탁하고 있습니다.</Paragraph>

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>수탁업체</th>
                  <th>위탁업무 내용</th>
                  <th>보유기간</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>구글 (Google LLC)</td>
                  <td>클라우드 인프라 운영 및 AI 운세 콘텐츠 생성</td>
                  <td>회원탈퇴 시 또는 위탁 계약 종료 시까지</td>
                </tr>
                <tr>
                  <td>포트원 (PortOne)</td>
                  <td>전자결제 대행 서비스</td>
                  <td>거래종료 후 5년</td>
                </tr>
                <tr>
                  <td>한국사이버결제(KCP)</td>
                  <td>전자결제 처리</td>
                  <td>거래종료 후 5년</td>
                </tr>
              </tbody>
            </Table>
          </TableWrapper>

          <InfoBox>
            <p>
              위탁업무의 내용이나 수탁자가 변경될 경우에는 지체없이 본 개인정보처리방침을 통하여 공개하도록 하겠습니다.
            </p>
          </InfoBox>
        </Section>

        <Section>
          <SectionTitle>제6조 (개인정보의 파기)</SectionTitle>
          <Paragraph>
            회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는
            지체없이 해당 개인정보를 파기합니다.
          </Paragraph>

          <SubSection>
            <SubTitle>파기 절차 및 방법</SubTitle>
            <List>
              <li><strong>전자적 파일</strong>: 복원이 불가능한 방법으로 영구 삭제</li>
              <li><strong>종이 문서</strong>: 분쇄 또는 소각</li>
              <li><strong>회원 탈퇴 시</strong>: 즉시 파기 (법령상 보존 의무가 있는 정보 제외)</li>
            </List>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>제7조 (정보주체의 권리·의무 및 행사방법)</SectionTitle>
          <Paragraph>정보주체는 회사에 대해 언제든지 다음의 개인정보 보호 관련 권리를 행사할 수 있습니다.</Paragraph>

          <List>
            <li><strong>개인정보 열람 요구</strong>: 본인의 개인정보를 열람하거나 제공받을 수 있습니다</li>
            <li><strong>개인정보 정정 요구</strong>: 오류가 있는 경우 정정을 요구할 수 있습니다</li>
            <li><strong>개인정보 삭제 요구</strong>: 개인정보의 삭제를 요구할 수 있습니다</li>
            <li><strong>개인정보 처리정지 요구</strong>: 개인정보의 처리 중지를 요구할 수 있습니다</li>
          </List>

          <SubSection>
            <SubTitle>권리 행사 방법</SubTitle>
            <Paragraph>
              위의 권리 행사는 아래 연락처로 서면, 전화, 이메일 등을 통하여 하실 수 있으며,
              회사는 이에 대해 지체없이 조치하겠습니다.
            </Paragraph>
            <List>
              <li>이메일: help@kindviral.com</li>
              <li>전화: 1661-2847</li>
              <li>팩스: 031-223-9093</li>
            </List>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>제8조 (개인정보의 안전성 확보조치)</SectionTitle>
          <Paragraph>
            회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취하고 있습니다.
          </Paragraph>

          <SubSection>
            <SubTitle>1. 기술적 보호조치</SubTitle>
            <List>
              <li>개인정보의 암호화 저장 및 전송</li>
              <li>비밀번호 단방향 암호화 보관</li>
              <li>접근 권한 관리 및 통제</li>
              <li>보안 프로그램 설치 및 주기적 갱신</li>
              <li>HTTPS 통신 암호화</li>
            </List>
          </SubSection>

          <SubSection>
            <SubTitle>2. 관리적 보호조치</SubTitle>
            <List>
              <li>개인정보 취급 직원의 최소화 및 교육</li>
              <li>개인정보보호 내부 관리계획 수립 및 시행</li>
              <li>정기적인 자체 점검 실시</li>
              <li>개인정보 처리시스템 접근 기록의 보관 및 위조·변조 방지</li>
            </List>
          </SubSection>

          <SubSection>
            <SubTitle>3. 물리적 보호조치</SubTitle>
            <List>
              <li>개인정보 보관 장소에 대한 출입 통제</li>
            </List>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>제9조 (개인정보 자동 수집 장치의 설치·운영 및 거부)</SectionTitle>
          <Paragraph>
            회사는 이용자에게 개별적인 맞춤서비스를 제공하기 위해 이용정보를 저장하고 수시로 불러오는
            '쿠키(cookie)'를 사용합니다.
          </Paragraph>

          <SubSection>
            <SubTitle>1. 쿠키의 사용 목적</SubTitle>
            <List>
              <li>로그인 세션 유지 및 회원 인증</li>
              <li>서비스 이용 편의성 제공</li>
            </List>
          </SubSection>

          <SubSection>
            <SubTitle>2. 쿠키의 설치·운영 및 거부 방법</SubTitle>
            <Paragraph>
              귀하는 쿠키 설치에 대한 선택권을 가지고 있습니다. 웹브라우저 옵션 설정을 통해
              모든 쿠키를 허용하거나, 쿠키가 저장될 때마다 확인을 거치거나, 모든 쿠키의 저장을 거부할 수 있습니다.
            </Paragraph>
            <List>
              <li><strong>Chrome</strong>: 설정 &gt; 개인정보 및 보안 &gt; 쿠키 및 기타 사이트 데이터</li>
              <li><strong>Safari</strong>: 환경설정 &gt; 개인정보 보호 &gt; 쿠키 및 웹사이트 데이터</li>
              <li><strong>Firefox</strong>: 설정 &gt; 개인정보 및 보안 &gt; 쿠키 및 사이트 데이터</li>
            </List>
            <InfoBox>
              <p>
                ※ 다만, 쿠키 설치를 거부하는 경우 로그인이 필요한 일부 서비스 이용에 어려움이 있을 수 있습니다.
              </p>
            </InfoBox>
          </SubSection>
        </Section>

        <Section>
          <SectionTitle>제10조 (개인정보보호책임자)</SectionTitle>
          <Paragraph>
            회사는 개인정보 처리에 관한 업무를 총괄해서 책임지고, 개인정보 처리와 관련한 정보주체의
            불만처리 및 피해구제 등을 위하여 아래와 같이 개인정보보호책임자를 지정하고 있습니다.
          </Paragraph>

          <ContactInfo>
            <ContactTitle>▶ 개인정보보호책임자</ContactTitle>
            <ContactList>
              <li>성명: 개인정보보호책임자</li>
              <li>직책: CPO</li>
              <li>연락처: 1661-2847</li>
              <li>이메일: privacy@kindviral.com</li>
            </ContactList>
          </ContactInfo>

          <ContactInfo>
            <ContactTitle>▶ 개인정보 보호담당자</ContactTitle>
            <ContactList>
              <li>부서명: 고객지원팀</li>
              <li>담당자: 고객지원팀장</li>
              <li>연락처: 1661-2847</li>
              <li>팩스: 031-223-9093</li>
              <li>이메일: help@kindviral.com</li>
            </ContactList>
          </ContactInfo>

          <Paragraph>
            정보주체는 회사의 서비스를 이용하시면서 발생한 모든 개인정보 보호 관련 문의, 불만처리,
            피해구제 등에 관한 사항을 개인정보보호책임자 및 담당부서로 문의하실 수 있습니다.
            회사는 정보주체의 문의에 대해 지체없이 답변 및 처리해드릴 것입니다.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>제11조 (권익침해 구제방법)</SectionTitle>
          <Paragraph>
            정보주체는 개인정보침해로 인한 구제를 받기 위하여 다음 기관에 분쟁해결이나 상담 등을 신청할 수 있습니다.
          </Paragraph>

          <List>
            <li>개인정보분쟁조정위원회: (국번없이) 1833-6972 (www.kopico.go.kr)</li>
            <li>개인정보침해신고센터: (국번없이) 118 (privacy.kisa.or.kr)</li>
            <li>대검찰청: (국번없이) 1301 (www.spo.go.kr)</li>
            <li>경찰청: (국번없이) 182 (ecrm.cyber.go.kr)</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>제12조 (개인정보처리방침 변경)</SectionTitle>
          <Paragraph>
            이 개인정보처리방침은 2025년 10월 7일부터 적용되며, 법령 및 방침에 따른 변경내용의 추가,
            삭제 및 정정이 있는 경우에는 변경사항의 시행 7일 전부터 공지사항을 통하여 고지할 것입니다.
          </Paragraph>

          <InfoBox>
            <p>
              <strong>부칙</strong><br />
              이 개인정보처리방침은 2025년 10월 7일부터 시행합니다.
            </p>
          </InfoBox>
        </Section>

        <BackButton to="/">← 홈으로 돌아가기</BackButton>
      </ContentWrapper>
    </Container>
  );
};